import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button,
  MenuItem,
  Fab,
  Icon,
  Switch
} from '@material-ui/core';
import { Formik, FieldArray } from 'formik';
import * as yup from 'yup';
import {
  generateCouponRuleApi,
  getStoreCategoriesApi,
  getStoreProductsApi,
  getStoreCustomerGroupsApi,
} from 'api';
import { BackdropCircular, InfoTooltip, BackTo } from 'components';
import Autocomplete from '@material-ui/lab/Autocomplete';


const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const defaultActionData = {
  attribute_name: 'sku',
  operator: '==',
  value: ''
};

const CreateCouponRuleAdvanced = ({loading}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories
  } = useSelector((state) => state.categories);
  const {store_products: {catalogProducts: products}, productsLoading} = useSelector((state) => state.products);
  const { customerGroups = [] } = useSelector((state) => state.customers);
  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];
  const { userStoreData } = useSelector((state) => state.user);

  useEffect(() => {
    if (!storeActiveCategoriesWithActiveNavLevel.length) {
      getStoreCategoriesApi({dispatch});
    }

    if (!products.length) {
      getStoreProductsApi({dispatch});
    }

    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);
  return (
    <div className="w-full pt-10 pb-25 min-h-full-screen scroll-y">
      <div className="max-w-1000 mx-auto">
        <BackTo
          link="/configurations/coupon-manager#coupon_rules"
          title={
            <div className="text-16 font-medium">
              {'Create New Advanced Coupon Rule ( '}
              <Link
                to="/coupon-manager/create-rule"
                className="text-primary underline text-14"
              >
                Simple Options
              </Link>
              {' )'}
            </div>
          }
        />
      </div>
      {customerGroups.length !== 0 &&
      <Formik
        initialValues={{
          is_active: true,
          name: '',
          description: '',
          uses_per_coupon: 0,
          expiration_days: 0,
          discount_amount: 0,
          discount_step: 0,
          simple_action: 'by_percent',
          simple_free_shipping: '0',
          customer_group_ids: [],
          website_ids: userStoreData?.storeId ? [userStoreData.storeId] : [],
          actions: {
            aggregator_type: 'all',
            value: 1,
            conditions: []
          }
        }}
        enableReinitialize
        validationSchema={
          yup.object({
            name: yup
              .string()
              .required('Field is required'),
            uses_per_coupon: yup
              .number()
              .integer('Only integer numbers are allowed')
              .min(0, 'Only positive numbers are allowed'),
            expiration_days: yup
              .number()
              .integer('Only integer numbers are allowed')
              .min(0, 'Only positive numbers are allowed'),
            discount_amount: yup
              .number()
              .min(0, 'Only positive numbers are allowed')
              .required('Field is required'),
            discount_step: yup
              .number()
              .min(0, 'Only positive numbers are allowed'),
            customer_group_ids: yup
              .array()
              .min(1, 'Choose a customer group')
              .required('Field is required'),
            actions: yup
              .object()
              .shape({
                conditions: yup
                  .array()
                  .of(
                    yup.object().shape({
                      attribute_name: yup
                        .string()
                        .required('Field is required'),
                      operator: yup
                        .string()
                        .required('Field is required'),
                      value: yup
                        .string()
                        .required('Field is required')
                    }))
              })
          })
        }
        onSubmit={async (values) => {
          await generateCouponRuleApi({dispatch, couponRule: values});
          history.push('/configurations/coupon-manager#coupon_rules');
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate className="max-w-1000 mx-auto mt-8">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    General:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Is Active
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Switch
                    color="primary"
                    name="is_active"
                    checked={values.is_active}
                    onChange={(event) => setFieldValue('is_active', event.target.checked)}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Rule Name <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Name"
                    name="name"
                    className="max-w-600"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Description
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    multiline
                    fullWidth
                    rows={2}
                    variant="outlined"
                    label="Description"
                    name="description"
                    className="max-w-600"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{
                      className: 'p-0'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Uses per Coupon
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Uses per Coupon"
                    name="uses_per_coupon"
                    className="max-w-600"
                    value={values.uses_per_coupon}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.uses_per_coupon && Boolean(errors.uses_per_coupon)}
                    helperText={touched.uses_per_coupon && errors.uses_per_coupon}
                  />
                  <InfoTooltip title="Leave 0 for unlimited usage" />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Coupon Expiration (days)
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Coupon Expiration"
                    name="expiration_days"
                    className="max-w-600"
                    value={values.expiration_days}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.expiration_days && Boolean(errors.expiration_days)}
                    helperText={touched.expiration_days && errors.expiration_days}
                  />
                  <InfoTooltip title="Leave 0 for unlimited usage" />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Apply
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Apply"
                    name="simple_action"
                    className="max-w-600"
                    value={values.simple_action}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.simple_action && Boolean(errors.simple_action)}
                    helperText={touched.simple_action && errors.simple_action}
                  >
                    <MenuItem value="by_percent">Percent of product price discount</MenuItem>
                    <MenuItem value="by_fixed">Fixed amount discount</MenuItem>
                    <MenuItem value="cart_fixed">Fixed amount discount for whole cart</MenuItem>
                    <MenuItem value="buy_x_get_y">Buy X get Y free (discount amount is Y)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Discount Amount <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Discount Amount"
                    name="discount_amount"
                    className="max-w-600"
                    value={values.discount_amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.discount_amount && Boolean(errors.discount_amount)}
                    helperText={touched.discount_amount && errors.discount_amount}
                  />
                </Grid>
                {values.simple_action === 'buy_x_get_y' &&
                  <>
                    <Grid item xs={12} sm={4} md={3}>
                      Discount Qty Step (Buy X)
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Discount Qty Step (Buy X)"
                        name="discount_step"
                        className="max-w-600"
                        value={values.discount_step}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.discount_step && Boolean(errors.discount_step)}
                        helperText={touched.discount_step && errors.discount_step}
                      />
                    </Grid>
                  </>
                }
                <Grid item xs={12} sm={4} md={3}>
                  Free Shipping
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    select
                    variant="outlined"
                    label="Free Shipping"
                    name="simple_free_shipping"
                    className="max-w-600"
                    value={values.simple_free_shipping}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.simple_free_shipping && Boolean(errors.simple_free_shipping)}
                    helperText={touched.simple_free_shipping && errors.simple_free_shipping}
                  >
                    <MenuItem value="0">No</MenuItem>
                    <MenuItem value="1">For matching items only</MenuItem>
                    <MenuItem value="2">For shipment with matching items</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  Customer Groups <span className="text-error">*</span>
                </Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <Autocomplete
                    multiple
                    fullWidth
                    disableClearable
                    size="small"
                    className="max-w-600"
                    name="customer_group_ids"
                    options={allCustomerGroups}
                    getOptionLabel={(option) => option.code}
                    onChange={(_, value, action, option) => {
                      if (action === 'select-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            [...values.customer_group_ids, option.option.id]
                            : values.customer_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            values.customer_group_ids.filter((id) => id !== option.option.id)
                            : values.customer_group_ids
                        );
                      }
                    }}
                    onBlur={(e, value, action, option) => {
                      if (action === 'select-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            [...values.customer_group_ids, option.option.id]
                            : values.customer_group_ids
                        );
                      }

                      if (action === 'remove-option') {
                        setFieldValue(
                          'customer_group_ids',
                          value !== null ?
                            values.customer_group_ids.filter((id) => id !== option.option.id)
                            : values.customer_group_ids
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        variant="outlined"
                        label="Customer Groups"
                        placeholder="Choose customer group"
                        error={touched.customer_group_ids && Boolean(errors.customer_group_ids)}
                        helperText={touched.customer_group_ids && errors.customer_group_ids}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h5">
                    Rule Actions:
                    <InfoTooltip title="You can skip these fields if you don't want to add additional conditions" />
                  </Typography>
                </Grid>
                <FieldArray name="actions.conditions">
                  {({remove, push}) => (
                    <>
                      {!values.actions?.conditions?.length ?
                        <Fab
                          color="primary"
                          size="small"
                          className="m-4"
                          onClick={() => push(defaultActionData)}
                          title="Add new rule action"
                        >
                          <Icon>add</Icon>
                        </Fab>
                        :
                        <>
                          <Typography variant="body1" className="pl-3 pb-3 text-16 text-muted">
                            If{' '}
                            <TextField
                              select
                              name="actions.aggregator_type"
                              value={values.actions.aggregator_type}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{ className: 'py-1 mx-2' }}
                            >
                              <MenuItem value="all">ALL</MenuItem>
                              <MenuItem value="any">ANY</MenuItem>
                            </TextField>{' '}
                            of next conditions are{' '}
                            <TextField
                              select
                              name="actions.value"
                              value={values.actions.value}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{ className: 'py-1 mx-2' }}
                            >
                              <MenuItem value="1">TRUE</MenuItem>
                              <MenuItem value="0">FALSE</MenuItem>
                            </TextField>{' '}
                            :
                          </Typography>
                          {values.actions.conditions.map((_, idx) => {
                            return (
                              <Grid  item xs={12} key={idx}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={4} md={3}>
                                    <TextField
                                      fullWidth
                                      select
                                      label="Select Type"
                                      name={`actions.conditions.${idx}.attribute_name`}
                                      value={values.actions.conditions[idx].attribute_name}
                                      onChange={(event) => {
                                        handleChange(event);
                                        setFieldValue(`actions.conditions.${idx}.operator`, '');
                                        setFieldValue(`actions.conditions.${idx}.value`, '');
                                      }}
                                      onBlur={(event) => {
                                        handleBlur(event);
                                        setFieldValue(`actions.conditions.${idx}.operator`, '');
                                        setFieldValue(`actions.conditions.${idx}.value`, '');
                                      }}
                                      error={touched.actions?.conditions?.length &&
                                          errors.actions?.conditions?.length &&
                                          touched?.actions.conditions[idx]?.attribute_name &&
                                          Boolean(errors?.actions.conditions[idx]?.attribute_name)
                                      }
                                      helperText={touched.actions?.conditions?.length &&
                                          errors.actions?.conditions?.length &&
                                          touched?.actions.conditions[idx]?.attribute_name &&
                                          errors?.actions.conditions[idx]?.attribute_name
                                      }
                                    >
                                      <MenuItem value="sku">SKU</MenuItem>
                                      <MenuItem value="category_ids">Category</MenuItem>
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={4} md={3}>
                                    {values.actions.conditions[idx].attribute_name === 'category_ids' &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Operator"
                                        name={`actions.conditions.${idx}.operator`}
                                        value={values.actions.conditions[idx].operator}
                                        onChange={(event) => {
                                          handleChange(event);
                                          setFieldValue(`actions.conditions.${idx}.value`, '');
                                        }}
                                        onBlur={(event) => {
                                          handleBlur(event);
                                          setFieldValue(`actions.conditions.${idx}.value`, '');
                                        }}
                                        error={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.operator &&
                                            Boolean(errors?.actions.conditions[idx]?.operator)
                                        }
                                        helperText={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.operator &&
                                            errors?.actions.conditions[idx]?.operator
                                        }
                                      >
                                        <MenuItem value="==">is</MenuItem>
                                        <MenuItem value="!=">is not</MenuItem>
                                        <MenuItem value="()">is one of</MenuItem>
                                        <MenuItem value="!()">is not one of</MenuItem>
                                      </TextField>
                                    }
                                    {(
                                      values.actions.conditions[idx].attribute_name === 'sku'
                                      || values.actions.conditions[idx].attribute_name === ''
                                    ) &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Operator"
                                        name={`actions.conditions.${idx}.operator`}
                                        value={values.actions.conditions[idx].operator}
                                        onChange={(event) => {
                                          handleChange(event);
                                          setFieldValue(`actions.conditions.${idx}.value`, '');
                                        }}
                                        onBlur={(event) => {
                                          handleBlur(event);
                                          setFieldValue(`actions.conditions.${idx}.value`, '');
                                        }}
                                        error={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.operator &&
                                            Boolean(errors?.actions.conditions[idx]?.operator)
                                        }
                                        helperText={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.operator &&
                                            errors?.actions.conditions[idx]?.operator
                                        }
                                      >
                                        <MenuItem value="==">is</MenuItem>
                                        <MenuItem value="!=">is not</MenuItem>
                                        <MenuItem value="{}">contain</MenuItem>
                                        <MenuItem value="!{}">does not contain</MenuItem>
                                        <MenuItem value="()">is one of</MenuItem>
                                        <MenuItem value="!()">is not one of</MenuItem>
                                      </TextField>
                                    }
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={5}>
                                    {(values.actions.conditions[idx].attribute_name === 'category_ids' &&
                                      values.actions.conditions[idx].operator.includes('=')) &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.conditions.${idx}.value`}
                                        value={values.actions.conditions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.value &&
                                            Boolean(errors?.actions.conditions[idx]?.value)
                                        }
                                        helperText={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.value &&
                                            errors?.actions.conditions[idx]?.value
                                        }
                                      >
                                        {storeActiveCategoriesWithActiveNavLevel.map((category) => {
                                          return (
                                            <MenuItem value={category.id} key={category.id}>
                                              {category.nameWithPath}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    }
                                    {(values.actions.conditions[idx].attribute_name === 'sku' &&
                                    values.actions.conditions[idx].operator.includes('{}')) &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.conditions.${idx}.value`}
                                        value={values.actions.conditions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.conditions?.length &&
                                          errors.actions?.conditions?.length &&
                                          touched?.actions.conditions[idx]?.value &&
                                          Boolean(errors?.actions.conditions[idx]?.value)
                                        }
                                        helperText={touched.actions?.conditions?.length &&
                                          errors.actions?.conditions?.length &&
                                          touched?.actions.conditions[idx]?.value &&
                                          errors?.actions.conditions[idx]?.value
                                        }
                                      >
                                        <MenuItem value="PROD">PROD</MenuItem>
                                        <MenuItem value="TREAT">TREAT</MenuItem>
                                        <MenuItem value="GIFT">GIFT</MenuItem>
                                        <MenuItem value="MEMBER">MEMBER</MenuItem>
                                      </TextField>
                                    }
                                    {(values.actions.conditions[idx].attribute_name === 'sku' &&
                                      values.actions.conditions[idx].operator.includes('=')) &&
                                      <TextField
                                        fullWidth
                                        select
                                        label="Value"
                                        name={`actions.conditions.${idx}.value`}
                                        value={values.actions.conditions[idx].value}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.value &&
                                            Boolean(errors?.actions.conditions[idx]?.value)
                                        }
                                        helperText={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched?.actions.conditions[idx]?.value &&
                                            errors?.actions.conditions[idx]?.value
                                        }
                                      >
                                        {storeActiveCategoriesWithActiveNavLevel.map((product) => {
                                          return (
                                            <MenuItem value={product.sku} key={product.sku}>
                                              {`${product.sku} (${product.name})`}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    }
                                    {(values.actions.conditions[idx].attribute_name === 'sku' &&
                                      values.actions.conditions[idx].operator.includes('()')) &&
                                      !!products.length &&
                                      <Autocomplete
                                        multiple
                                        fullWidth
                                        disableClearable
                                        size="small"
                                        name={`actions.conditions.${idx}.value`}
                                        options={products}
                                        getOptionLabel={(option) => `${option.name} (${option.sku})`}
                                        defaultValue={products.filter((product) => values.actions.conditions[idx].value.split(', ').includes(String(product.sku)))}
                                        onChange={(_, value, action, option) => {
                                          if (action === 'select-option') {
                                            setFieldValue(
                                              `actions.conditions.${idx}.value`,
                                              value.length != 0 ?
                                                [...values.actions.conditions[idx].value.split(', '), String(option.option.sku)].join(', ')
                                                : value.map((v) => v.id).join('')
                                            );
                                          }

                                          if (action === 'remove-option') {
                                            const filteredValues = values.actions.conditions[idx].value.split(', ').filter((sku) => sku != option.option.sku);
                                            setFieldValue(
                                              `actions.conditions.${idx}.value`,
                                              filteredValues.join(', ')
                                            );
                                          }
                                        }}
                                        onBlur={(e, value, action, option) => {
                                          touched.actions.conditions[idx].value = true;

                                          if (action === 'select-option') {
                                            if (action === 'select-option') {
                                              setFieldValue(
                                                `actions.conditions.${idx}.value`,
                                                value.length != 1 ?
                                                  [...values.actions.conditions[idx].value.split(', '), String(option.option.sku)].join(', ')
                                                  : value.map((v) => v.id).join('')
                                              );
                                            }

                                            if (action === 'remove-option') {
                                              const filteredValues = values.actions.conditions[idx].value.split(', ').filter((sku) => sku != option.option.sku);
                                              setFieldValue(
                                                `actions.conditions.${idx}.value`,
                                                filteredValues.join(', ')
                                              );
                                            }
                                          }
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="Value"
                                            style={{paddingTop: '3px'}}
                                            error={touched.actions?.conditions?.length &&
                                              errors.actions?.conditions?.length &&
                                              touched.actions?.conditions[idx]?.value &&
                                              Boolean(errors.actions?.conditions[idx]?.value)
                                            }
                                            helperText={touched.actions?.conditions?.length &&
                                                errors.actions?.conditions?.length &&
                                                touched.actions?.conditions[idx]?.value &&
                                                errors.actions?.conditions[idx]?.value
                                            }
                                          />
                                        )}
                                      />
                                    }
                                    {(values.actions.conditions[idx].attribute_name === 'category_ids' &&
                                      values.actions.conditions[idx].operator.includes('()')) &&
                                      !!storeActiveCategoriesWithActiveNavLevel.length &&
                                      <Autocomplete
                                        multiple
                                        fullWidth
                                        disableClearable
                                        size="small"
                                        name={`actions.conditions.${idx}.value`}
                                        options={storeActiveCategoriesWithActiveNavLevel}
                                        getOptionLabel={(option) => option.nameWithPath}
                                        defaultValue={storeActiveCategoriesWithActiveNavLevel.filter((category) => values.actions.conditions[idx].value.split(', ').includes(String(category.id)))}
                                        onChange={(_, value, action, option) => {
                                          if (action === 'select-option') {
                                            setFieldValue(
                                              `actions.conditions.${idx}.value`,
                                              value.length != 1 ?
                                                [...values.actions.conditions[idx].value.split(', '), String(option.option.id)].join(', ')
                                                : value.map((v) => v.id).join('')
                                            );
                                          }

                                          if (action === 'remove-option') {
                                            const filteredValues = values.actions.conditions[idx].value.split(', ').filter((id) => id != option.option.id);
                                            setFieldValue(
                                              `actions.conditions.${idx}.value`,
                                              filteredValues.join(', ')
                                            );
                                          }
                                        }}
                                        onBlur={(e, value, action, option) => {
                                          handleBlur(e);

                                          if (action === 'select-option') {
                                            if (action === 'select-option') {
                                              setFieldValue(
                                                `actions.conditions.${idx}.value`,
                                                value.length != 1 ?
                                                  [...values.actions.conditions[idx].value.split(', '), String(option.option.id)].join(', ')
                                                  : value.map((v) => v.id).join('')
                                              );
                                            }

                                            if (action === 'remove-option') {
                                              const filteredValues = values.actions.conditions[idx].value.split(', ').filter((id) => id != option.option.id);
                                              setFieldValue(
                                                `actions.conditions.${idx}.value`,
                                                filteredValues.join(', ')
                                              );
                                            }
                                          }
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="Value"
                                            style={{paddingTop: '3px'}}
                                            error={touched.actions?.conditions?.length &&
                                            errors.actions?.conditions?.length &&
                                            touched.actions?.conditions[idx]?.value &&
                                            Boolean(errors.actions?.conditions[idx]?.value)
                                            }
                                            helperText={touched.actions?.conditions?.length &&
                                              errors.actions?.conditions?.length &&
                                              touched.actions?.conditions[idx]?.value &&
                                              errors.actions?.conditions[idx]?.value
                                            }
                                          />
                                        )}
                                      />
                                    }
                                  </Grid>
                                  <Grid item xs={12} sm={2} md={1}>
                                    <Fab
                                      size="small"
                                      className="m-2"
                                      onClick={() => remove(idx)}
                                      title="Remove rule action"
                                    >
                                      <Icon>delete</Icon>
                                    </Fab>
                                  </Grid>
                                </Grid>
                              </Grid>
                            );
                          })}
                          <Fab
                            color="primary"
                            size="small"
                            className="m-2"
                            onClick={() => push(defaultActionData)}
                            title="Add new rule action"
                          >
                            <Icon>add</Icon>
                          </Fab>
                        </>
                      }
                    </>
                  )}
                </FieldArray>
                <Grid item xs={12} className="pt-8">
                  <Button
                    color="primary"
                    variant="contained"
                    className="mr-4"
                    type="submit"
                  >
                    Create Coupon Rule
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => history.push('/configurations/coupon-manager#coupon_rules')}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
      }
      <BackdropCircular
        loading={loading || loadingCategories || productsLoading}
        title="Preparing values..."
      />
    </div>
  );
};

export default CreateCouponRuleAdvanced;
